import React from "react"
import { Alert, BackIcon, Box, Button, Link, Typography, useTheme } from "@carrotfertility/carotene-core"
import {
  Form,
  FormAutocomplete,
  FormButton,
  FormTextField,
  useFormContext,
  useFormState,
  useWatch
} from "@carrotfertility/carotene-core-x"
import { UsaStatesAndDc } from "@internal/constants"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import { InsuranceProviderField } from "./InsuranceProviderField"
import type { CoverageDetails } from "./types"
import type { InsuranceError, InsuranceInputs } from "../../insuranceTypes"

const memberIdMessages = defineMessages({
  NOT_FOUND_MEMBER_ID: {
    defaultMessage: "No insurance plans match the Member ID you entered."
  },
  CONFLICT_MEMBER_ID: {
    defaultMessage: "Multiple insurance plans match the details you entered. Check your Member ID."
  }
})

type Props = {
  inputs?: InsuranceInputs | null
  verificationError?: InsuranceError | null
  onExit?: () => void
  memberReady: boolean
  onSubmit: (inputs: InsuranceInputs) => Promise<void>
}

export function InsuranceDetailsStep({ inputs, verificationError, onExit, memberReady, onSubmit }: Props): JSX.Element {
  const intl = useIntl()
  const stateOptions = UsaStatesAndDc.map((state) => ({ value: state.id, label: intl.formatMessage(state.label) }))
  const details: CoverageDetails = {
    state: stateOptions.find((state) => state.value === inputs?.state) ?? null,
    insuranceProvider: inputs ? { value: inputs.payerId, label: inputs.payerName } : null,
    insuranceMemberId: inputs?.memberId ?? ""
  }
  const required = intl.formatMessage({ defaultMessage: "Required" })
  const theme = useTheme()

  return (
    <Box sx={{ inlineSize: "100%" }}>
      <Typography variant="h3" component="h1" marginBottom={theme.spacing(theme.tokens.spacing.sm)}>
        <FormattedMessage defaultMessage="Let's check your coverage" />
      </Typography>
      <Typography marginBottom={theme.spacing(theme.tokens.spacing.lg)}>
        <FormattedMessage defaultMessage="Enter your insurance details and we'll estimate what you'll pay for this visit." />
      </Typography>
      {!memberReady && (
        <Typography role="alert">
          <FormattedMessage defaultMessage="Your legal name, date of birth, and visit configuration are needed to check coverage. Please review your profile or contact your Carrot Companion." />
        </Typography>
      )}
      <Box sx={{ inlineSize: "100%" }}>
        <Form<CoverageDetails>
          mode="onChange"
          defaultValues={details}
          onSubmit={(values) => {
            if (values.state && values.insuranceProvider)
              void onSubmit({
                state: String(values.state.value),
                payerId: String(values.insuranceProvider.value),
                payerName: String(values.insuranceProvider.label),
                memberId: values.insuranceMemberId.trim()
              })
          }}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(theme.tokens.spacing.lg)
            })}
          >
            <InsuranceProviderField />
            <InsuranceMemberIdField inputs={inputs} verificationError={verificationError} required={required} />
            <FormAutocomplete
              name="state"
              label={intl.formatMessage({ defaultMessage: "The state where you'll be during the appointment" })}
              options={stateOptions}
              fullWidth
              controllerProps={{ rules: { required } }}
            />
            {verificationError && (
              <Alert severity="error">
                <FormattedMessage
                  defaultMessage="We couldn't verify your insurance. Try again, check your insurance info, or <link>send us a message</link>"
                  values={{
                    link: (chunks) => (
                      <Link color="inherit" component={RouterLink} to="/messages">
                        {chunks}
                      </Link>
                    )
                  }}
                />
              </Alert>
            )}
            <Box
              sx={(theme) => ({
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                gap: theme.spacing(theme.tokens.spacing.md)
              })}
            >
              <Button type="button" variant="text" color="secondary" startIcon={<BackIcon />} onClick={onExit}>
                <FormattedMessage defaultMessage="Back" />
              </Button>
              <CheckCoverageButton memberReady={memberReady} />
            </Box>
          </Box>
        </Form>
      </Box>
    </Box>
  )
}

function InsuranceMemberIdField({
  inputs,
  verificationError,
  required
}: Pick<Props, "inputs" | "verificationError"> & { required: string }): JSX.Element {
  const intl = useIntl()
  const { trigger } = useFormContext<CoverageDetails>()
  const memberId = useWatch<CoverageDetails, "insuranceMemberId">({ name: "insuranceMemberId" })
  const provider = useWatch<CoverageDetails, "insuranceProvider">({ name: "insuranceProvider" })
  const state = useWatch<CoverageDetails, "state">({ name: "state" })
  const code = verificationError?.code
  const message = code === "NOT_FOUND_MEMBER_ID" || code === "CONFLICT_MEMBER_ID" ? memberIdMessages[code] : undefined
  // Bridge's feedback applies only to the submitted values. Editing the ID, payer, or state
  // clears that response so the member can make a fresh attempt.
  const fieldError =
    message &&
    inputs &&
    memberId.trim() === inputs.memberId.trim() &&
    provider?.value === inputs.payerId &&
    state?.value === inputs.state
      ? intl.formatMessage(message)
      : undefined

  React.useEffect(() => {
    if (message) void trigger("insuranceMemberId")
  }, [fieldError, message, trigger])

  return (
    <FormTextField
      name="insuranceMemberId"
      label={intl.formatMessage({ defaultMessage: "Member ID" })}
      placeholder={intl.formatMessage({ defaultMessage: "e.g., XJ4 820 551" })}
      helperText={intl.formatMessage({ defaultMessage: "The ID number printed on your insurance card." })}
      fullWidth
      registerOptions={{ required, validate: (value: string) => (!value.trim() ? required : fieldError ?? true) }}
    />
  )
}

function CheckCoverageButton({ memberReady }: { memberReady: boolean }): JSX.Element {
  const { isValid } = useFormState<CoverageDetails>()
  return (
    <FormButton disabled={!memberReady || !isValid} type="submit" variant="contained" color="secondary">
      <FormattedMessage defaultMessage="Check coverage" />
    </FormButton>
  )
}
